import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faLightbulb, faPuzzlePiece, faVolumeHigh } from "@fortawesome/free-solid-svg-icons";
import {
    localizedText,
    type KanjiMathematicsEntry,
    type KanjiMathematicsExplanationType,
    type KanjiMathematicsPart,
} from "../../consts/kanjiMathematics";
import KanjiEquation from "./KanjiEquation";
import styles from "./kanjiMathematicsPage.module.scss";

const typeIcons: Record<KanjiMathematicsExplanationType, IconDefinition> = {
    structural: faPuzzlePiece,
    mnemonic: faLightbulb,
    phonetic: faVolumeHigh,
};

function uniqueBySymbol(parts: readonly KanjiMathematicsPart[]): KanjiMathematicsPart[] {
    return parts.filter((part, index) => parts.findIndex((other) => other.symbol === part.symbol) === index);
}

function PartRow({ part, language, isResult }: { part: KanjiMathematicsPart; language: string; isResult?: boolean }) {
    return (
        <li className={isResult ? `${styles.part} ${styles.partResult}` : styles.part}>
            <span className={styles.partKanji} lang="ja">{part.symbol}</span>
            <span className={styles.partDetails}>
                <span className={styles.partReading}>
                    <span lang="ja">{part.hiragana}</span> / {part.romaji}
                </span>
                <span className={styles.partMeaning}>{localizedText(part.meaning, language)}</span>
            </span>
        </li>
    );
}

export default function KanjiMathematicsCard({ entry }: { entry: KanjiMathematicsEntry }) {
    const { t, i18n } = useTranslation("kanjiMathematicsPage");
    const language = i18n.language;
    const titleId = `kanji-mathematics-${entry.id}`;
    const note = entry.note ? localizedText(entry.note, language) : null;

    return (
        <article className={styles.card} aria-labelledby={titleId}>
            <header className={styles.cardHeader}>
                <h2 id={titleId} className={styles.cardTitle}>{localizedText(entry.title, language)}</h2>
                <ul className={styles.tags}>
                    <li className={styles.tag}>{t(`categories.${entry.category}`)}</li>
                    {entry.jlptLevel && <li className={styles.tag}>{t("jlpt", { level: entry.jlptLevel })}</li>}
                </ul>
            </header>

            <div className={styles.equationBox}>
                <KanjiEquation entry={entry} />
            </div>

            <ul className={styles.parts} aria-label={t("partsLabel")}>
                {uniqueBySymbol(entry.components).map((part) => (
                    <PartRow key={part.symbol} part={part} language={language} />
                ))}
                <PartRow part={entry.result} language={language} isResult />
            </ul>

            <p className={`${styles.type} ${styles[`type_${entry.explanationType}`]}`}>
                <span className={styles.typeBadge}>
                    <FontAwesomeIcon icon={typeIcons[entry.explanationType]} />
                    {t(`types.${entry.explanationType}.label`)}
                </span>
                <span className={styles.typeDescription}>{t(`types.${entry.explanationType}.description`)}</span>
            </p>
            <p className={styles.explanation}>{localizedText(entry.explanation, language)}</p>
            {note && <p className={styles.note}>{note}</p>}
        </article>
    );
}
